import { useState } from 'react';
import { useNavigate, Navigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../store/auth';
import { useBranding } from '../store/branding';
import { appConfig } from '../lib/app-config';
import BrandLogo from '../components/BrandLogo';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

// Sized to match the Filament admin login: 36px controls, 14px text, 8px radius.
const inputClass =
  'flex h-9 w-full rounded-lg border border-input bg-card px-3 py-1.5 text-sm transition-colors placeholder:text-muted-foreground focus-visible:border-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user, checked } = useAuth();
  const { refresh: refreshBranding } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const passwordReset = (location.state as { passwordReset?: boolean } | null)?.passwordReset;

  if (user && checked) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const safetyTimeout = setTimeout(() => {
      setLoading(false);
      setError(
        'Request is taking too long. On a device/simulator, set VITE_API_URL to your computer’s IP (e.g. http://192.168.1.x:8001), then rebuild.'
      );
    }, 14_000);
    try {
      const result = await login(email, password, remember);
      clearTimeout(safetyTimeout);
      if (result.error) setError(result.error);
      else {
        await refreshBranding();
        navigate('/dashboard');
      }
    } catch (err) {
      clearTimeout(safetyTimeout);
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const apiUrl = import.meta.env.VITE_API_URL || '';

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-muted/50 p-6 pt-safe pb-safe">
      <Card className="w-full max-w-[22rem] shadow-sm">
        <CardContent className="p-8">
          <BrandLogo className="mb-6" width={288} />

          <form onSubmit={handleSubmit} className="space-y-4">
            {passwordReset && (
              <div className="rounded-lg border border-success/20 bg-success/10 px-3 py-2 text-sm text-success">
                Password reset successfully. Sign in with your new password.
              </div>
            )}
            {error && (
              <div className="rounded-lg border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm text-destructive whitespace-pre-wrap">
                {error}
              </div>
            )}
            {import.meta.env.DEV && apiUrl && (
              <p className="truncate text-center text-xs text-muted-foreground" title={apiUrl}>
                API: {apiUrl}
              </p>
            )}
            <div>
              <label htmlFor="email" className="mb-1 block text-sm font-medium text-foreground">
                Email address<sup className="text-destructive">*</sup>
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
                required
              />
            </div>
            <div>
              <label htmlFor="password" className="mb-1 block text-sm font-medium text-foreground">
                Password<sup className="text-destructive">*</sup>
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${inputClass} pr-10`}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-4 w-4 rounded border-input text-primary accent-[hsl(var(--primary))] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
              />
              Remember me
            </label>
            <Button type="submit" disabled={loading} className="h-9 w-full text-sm">
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              <Link to="/forgot-password" className="font-medium text-primary hover:underline">
                Forgot password?
              </Link>
            </p>

            {appConfig.isEmployeeWeb && (
              <p className="text-center text-sm text-muted-foreground">
                Admin?{' '}
                <a href="/admin/login" className="font-medium text-primary hover:underline">
                  Sign in to admin
                </a>
              </p>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
